/**
 * The Glass — catalog pane search (wave glass-6, #805).
 *
 * The lookup verb's resolution behavior (engineering/contract/bin/
 * catalog-lookup.py), reproduced client-side over the packaged catalog canon.
 * Pure functions, exported for tests. The behavior reproduced, per that
 * script's header: case-insensitive; names are id / name / aliases / ref /
 * files plus the bare name after a `kind:` prefix, and — exact tier only — a
 * term's glossary avoid entries (#1145); exact match first,
 * substring only when nothing exact hits, multi-token (every token in one
 * name) only when nothing else hits. More than 8 hits is a narrow-the-name
 * list; LIST_CAP caps every listing at 40. No judgment is added here — a
 * divergence from the script's resolution order is a defect, not a choice.
 */

import type {
  GlassCatalog,
  GlassCatalogCard,
  GlassCatalogEdge,
  GlassRegisterRow,
} from "@command-center/shared";

export const LIST_CAP = 40;
export const NARROW_THRESHOLD = 8;

/** Whitespace-collapsed single line, truncated with an ellipsis — the
 *  script's one_line(). */
export function oneLine(text: string | null | undefined, limit = 160): string {
  const t = (text ?? "").split(/\s+/).join(" ").trim();
  return t.length <= limit ? t : t.slice(0, limit - 1) + "…";
}

/** Every name a card answers to, lowercased — id, name, aliases, ref, files. */
function cardNames(card: GlassCatalogCard): string[] {
  const names = [card.id, card.name, ...card.aliases];
  if (card.ref !== undefined) names.push(card.ref);
  if (card.files !== undefined) names.push(...card.files);
  return names.filter((n) => n !== "").map((n) => n.toLowerCase());
}

/**
 * The entry of a term card's glossary `avoid` list equal to the query
 * (case-insensitive, trimmed), or undefined — the script's avoid_hit()
 * (#1145). An avoid-word is an exact name only: it never joins cardNames(),
 * so it feeds neither the substring nor the multi-token tier.
 */
export function avoidHit(
  card: GlassCatalogCard,
  query: string,
): string | undefined {
  const q = query.trim().toLowerCase();
  if (q === "") return undefined;
  return (card.avoid ?? []).find((w) => w.trim().toLowerCase() === q);
}

/**
 * The script's match(): exact id/name/alias/ref/file — including the bare
 * name after a `kind:` prefix — before substring, before multi-word
 * every-token-in-one-name. Each tier returns only when every prior tier is
 * empty.
 */
export function matchCards(
  cards: readonly GlassCatalogCard[],
  query: string,
): GlassCatalogCard[] {
  const q = query.trim().toLowerCase();
  if (q === "") return [];
  const tokens = q.split(/\s+/).filter((t) => t !== "");
  const exact: GlassCatalogCard[] = [];
  const sub: GlassCatalogCard[] = [];
  const token: GlassCatalogCard[] = [];
  for (const c of cards) {
    const lower = cardNames(c);
    const bare = lower
      .filter((n) => n.includes(":"))
      .map((n) => n.slice(n.indexOf(":") + 1));
    if (lower.includes(q) || bare.includes(q) || avoidHit(c, q) !== undefined) {
      exact.push(c);
    } else if (lower.some((n) => n.includes(q))) {
      sub.push(c);
    } else if (
      tokens.length > 1 &&
      lower.some((n) => tokens.every((t) => n.includes(t)))
    ) {
      token.push(c);
    }
  }
  if (exact.length > 0) return exact;
  if (sub.length > 0) return sub;
  return token;
}

export interface EdgeIndex {
  byFrom: Map<string, GlassCatalogEdge[]>;
  byTo: Map<string, GlassCatalogEdge[]>;
  byId: Map<string, GlassCatalogCard>;
}

export function buildEdgeIndex(catalog: GlassCatalog): EdgeIndex {
  const byFrom = new Map<string, GlassCatalogEdge[]>();
  const byTo = new Map<string, GlassCatalogEdge[]>();
  const byId = new Map<string, GlassCatalogCard>();
  for (const c of catalog.cards) byId.set(c.id, c);
  for (const e of catalog.edges) {
    const f = byFrom.get(e.from);
    if (f === undefined) byFrom.set(e.from, [e]);
    else f.push(e);
    const t = byTo.get(e.to);
    if (t === undefined) byTo.set(e.to, [e]);
    else t.push(e);
  }
  return { byFrom, byTo, byId };
}

/** Edges grouped by type, type-sorted — the script's per-card edge listing. */
export function groupEdgesByType(
  edges: readonly GlassCatalogEdge[],
): [string, GlassCatalogEdge[]][] {
  const groups = new Map<string, GlassCatalogEdge[]>();
  for (const e of edges) {
    const g = groups.get(e.type);
    if (g === undefined) groups.set(e.type, [e]);
    else g.push(e);
  }
  return [...groups.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
}

/** The paths a card names that the register can answer for — ref (trailing
 *  slash stripped) then files, deduplicated — the script's card_paths. */
export function cardRegisterPaths(card: GlassCatalogCard): string[] {
  const paths: string[] = [];
  if (card.ref !== undefined) paths.push(card.ref.replace(/\/+$/, ""));
  if (card.files !== undefined) paths.push(...card.files);
  return [...new Set(paths)];
}

export interface RegisterLine {
  path: string;
  row: GlassRegisterRow | null; // null = contract/ path with no row: untouched
}

/** The script's register_lines(): a row's state line per registered path; a
 *  contract/ path with no row is named untouched; other rowless paths are
 *  silent. */
export function registerLines(
  paths: readonly string[],
  rowsByPath: ReadonlyMap<string, GlassRegisterRow>,
): RegisterLine[] {
  const out: RegisterLine[] = [];
  for (const p of paths) {
    const row = rowsByPath.get(p);
    if (row !== undefined) out.push({ path: p, row });
    else if (p.startsWith("engineering/contract/")) out.push({ path: p, row: null });
  }
  return out;
}

/** The script's no-card fallback: register paths the query substring-hits. */
export function registerFallback(
  query: string,
  rowsByPath: ReadonlyMap<string, GlassRegisterRow>,
): string[] {
  const qnorm = query.trim().replace(/^\/+|\/+$/g, "").toLowerCase();
  if (qnorm === "") return [];
  return [...rowsByPath.keys()]
    .filter((p) => p.toLowerCase().includes(qnorm))
    .sort()
    .slice(0, LIST_CAP);
}
